import { useState } from 'react';
import type { ResumeData, ResumeSettings, TemplateType } from '../types/resume';
import { LayoutGrid, FileText, Type, GripVertical, X, Upload } from 'lucide-react';

type SettingsTab = 'templates' | 'layout' | 'formatting';

const templateCategories = [
  { key: 'all', label: 'All' },
  { key: 'with-photo', label: 'With photo' },
  { key: 'single-column', label: 'Single column' },
  { key: 'two-column', label: 'Two column' },
  { key: 'ats', label: 'ATS' },
] as const;

interface TemplateDef {
  key: TemplateType;
  label: string;
  description: string;
  category: 'single-column' | 'two-column' | 'ats' | 'with-photo';
}

const templates: TemplateDef[] = [
  { key: 'standard', label: 'Standard', description: 'Clean, professional single-column layout', category: 'single-column' },
  { key: 'modern', label: 'Modern', description: 'Dark header with single-column body', category: 'single-column' },
  { key: 'classic', label: 'Classic', description: 'Traditional format with clean typography', category: 'single-column' },
  { key: 'professional', label: 'Professional', description: 'Balanced sections with subtle styling', category: 'single-column' },
  { key: 'executive', label: 'Executive', description: 'Bold header and structured layout', category: 'single-column' },
  { key: 'minimal', label: 'Minimal', description: 'Lightweight and easy to scan', category: 'single-column' },
  { key: 'technical', label: 'Technical', description: 'Skills-first layout for engineers', category: 'single-column' },
  { key: 'portfolio', label: 'Portfolio', description: 'Project-focused creative layout', category: 'single-column' },
  { key: 'elegant', label: 'Elegant', description: 'Refined serif typography', category: 'single-column' },
  { key: 'compact', label: 'Compact', description: 'Dense layout for experienced candidates', category: 'single-column' },
  { key: 'creative', label: 'Creative', description: 'Timeline-based design with visual accents', category: 'single-column' },
  { key: 'sidebar-right', label: 'Sidebar', description: 'Right sidebar for skills and contact', category: 'two-column' },
  { key: 'two-column', label: 'Two Column', description: 'Compact two-column design', category: 'two-column' },
  { key: 'clean-two-column', label: 'Clean Two Column', description: 'Clean sidebar with main content area', category: 'two-column' },
  { key: 'modern-sidebar', label: 'Modern Sidebar', description: 'Colored left sidebar with modern styling', category: 'two-column' },
  { key: 'photo', label: 'Photo', description: 'Modern layout with circular profile photo', category: 'with-photo' },
  { key: 'ats', label: 'ATS', description: 'Parser-friendly single-column format', category: 'ats' },
  { key: 'ats-compact', label: 'ATS Compact', description: 'Space-efficient ATS layout', category: 'ats' },
  { key: 'ats-serif', label: 'ATS Serif', description: 'ATS-friendly serif style', category: 'ats' },
];

const sectionLabels: Record<string, string> = {
  summary: 'Professional Summary',
  experience: 'Work Experience',
  education: 'Education',
  skills: 'Skills',
  projects: 'Projects',
  certifications: 'Certifications',
};

interface SettingsPanelProps {
  isOpen: boolean;
  onClose: () => void;
  data: ResumeData;
  template: TemplateType;
  setTemplate: (t: TemplateType) => void;
  settings: ResumeSettings;
  updateSettings: (u: Partial<ResumeSettings>) => void;
  updateSectionOrder: (order: string[]) => void;
  setPhoto: (photo?: string) => void;
}

export function SettingsPanel({
  isOpen,
  onClose,
  template,
  setTemplate,
  settings,
  updateSettings,
  updateSectionOrder,
  setPhoto,
}: SettingsPanelProps) {
  const [tab, setTab] = useState<SettingsTab>('templates');
  const [category, setCategory] = useState<string>('all');

  if (!isOpen) return null;

  const filteredTemplates = category === 'all'
    ? templates
    : templates.filter((t) => t.category === category);

  const moveSection = (index: number, direction: number) => {
    const order = [...settings.sectionOrder];
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= order.length) return;
    [order[index], order[newIndex]] = [order[newIndex], order[index]];
    updateSectionOrder(order);
  };

  const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => setPhoto(reader.result as string);
    reader.readAsDataURL(file);
  };

  const fontOptions = ['Inter', 'Arial', 'Georgia', 'Merriweather', 'Helvetica', 'Times New Roman'];

  return (
    <div className="resume-app rx-dark fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[85vh] flex flex-col border scrollbar-thin">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div className="flex items-center gap-6">
            {[
              { key: 'templates', label: 'Templates', icon: LayoutGrid },
              { key: 'layout', label: 'Layout', icon: FileText },
              { key: 'formatting', label: 'Formatting', icon: Type },
            ].map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key as SettingsTab)}
                className={`flex items-center gap-2 text-sm font-medium pb-3 -mb-4 border-b-2 transition-colors ${
                  tab === t.key
                    ? 'border-primary-600 text-primary-700'
                    : 'border-transparent text-slate-500 hover:text-slate-700'
                }`}
              >
                <t.icon className="h-4 w-4" /> {t.label}
              </button>
            ))}
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {tab === 'templates' && (
            <div className="space-y-5">
              <div className="flex flex-wrap gap-2">
                {templateCategories.map((cat) => (
                  <button
                    key={cat.key}
                    onClick={() => setCategory(cat.key)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                      category === cat.key
                        ? 'bg-primary-600 text-white'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    {cat.label}
                  </button>
                ))}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredTemplates.map((t) => (
                  <button
                    key={t.key}
                    onClick={() => setTemplate(t.key)}
                    className={`text-left rounded-xl border-2 p-4 transition-all ${
                      template === t.key
                        ? 'border-primary-600 bg-primary-50'
                        : 'border-slate-200 hover:border-primary-300 bg-white'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold text-slate-900">{t.label}</span>
                      {template === t.key && <span className="text-primary-600 text-xs font-medium">Selected</span>}
                    </div>
                    <p className="text-xs text-slate-500">{t.description}</p>
                  </button>
                ))}
              </div>
            </div>
          )}

          {tab === 'layout' && (
            <div className="space-y-6 max-w-xl">
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-slate-900">Format</h3>
                <select
                  value={settings.format}
                  onChange={(e) => updateSettings({ format: e.target.value as 'A4' | 'Letter' })}
                  className="w-full rounded-lg border-slate-200 px-3 py-2 text-sm"
                >
                  <option value="A4">A4 (8.27 x 11.69)</option>
                  <option value="Letter">Letter (8.5 x 11)</option>
                </select>
              </div>

              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-slate-900">Margins & paddings</h3>
                <div className="grid grid-cols-2 gap-4">
                  {(['top', 'bottom', 'left', 'right'] as const).map((side) => (
                    <label key={side} className="block">
                      <span className="text-xs text-slate-600 capitalize">{side} margin (in)</span>
                      <input
                        type="number"
                        step="0.1"
                        min="0"
                        max="2"
                        value={settings.margins[side]}
                        onChange={(e) => updateSettings({ margins: { ...settings.margins, [side]: parseFloat(e.target.value) || 0 } })}
                        className="mt-1 block w-full rounded-lg border-slate-200 px-3 py-2 text-sm"
                      />
                    </label>
                  ))}
                </div>
              </div>

              <div className="space-y-4">
                {[
                  { label: 'Between sections', key: 'sectionGap', max: 30, unit: 'pt' },
                  { label: 'Between titles & content', key: 'titleGap', max: 20, unit: 'pt' },
                  { label: 'Between content blocks', key: 'contentGap', max: 20, unit: 'pt' },
                ].map((item) => (
                  <div key={item.key} className="space-y-1">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-slate-600">{item.label}</span>
                      <span className="font-medium text-slate-800">{(settings.spacing as any)[item.key]} {item.unit}</span>
                    </div>
                    <input
                      type="range"
                      min="0"
                      max={item.max}
                      value={(settings.spacing as any)[item.key]}
                      onChange={(e) => updateSettings({ spacing: { ...settings.spacing, [item.key]: parseInt(e.target.value) } })}
                      className="w-full"
                    />
                  </div>
                ))}
              </div>

              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-slate-900">Date format</h3>
                <select
                  value={settings.dateFormat}
                  onChange={(e) => updateSettings({ dateFormat: e.target.value as 'short' | 'long' | 'numeric' })}
                  className="w-full rounded-lg border-slate-200 px-3 py-2 text-sm"
                >
                  <option value="short">Short Name (Jan YYYY)</option>
                  <option value="long">Long Name (January YYYY)</option>
                  <option value="numeric">Numeric (MM/YYYY)</option>
                </select>
              </div>

              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-slate-900">Header alignment</h3>
                <div className="flex gap-2">
                  {(['left', 'center', 'right'] as const).map((align) => (
                    <button
                      key={align}
                      onClick={() => updateSettings({ headerAlignment: align })}
                      className={`flex-1 py-2 rounded-lg text-xs font-medium capitalize transition-colors ${
                        settings.headerAlignment === align
                          ? 'bg-primary-600 text-white'
                          : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                      }`}
                    >
                      {align}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {tab === 'formatting' && (
            <div className="space-y-6 max-w-xl">
              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-slate-900">Profile photo</h3>
                <div className="flex items-center gap-3">
                  {settings.photo && (
                    <img src={settings.photo} alt="Profile" className="w-16 h-16 rounded-full object-cover border" />
                  )}
                  <label className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-medium cursor-pointer transition-colors">
                    <Upload className="h-4 w-4" /> Upload photo
                    <input type="file" accept="image/*" className="hidden" onChange={handlePhotoUpload} />
                  </label>
                  {settings.photo && (
                    <button onClick={() => setPhoto(undefined)} className="text-xs text-red-600 hover:text-red-700">Remove</button>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-900">Primary font</label>
                  <select
                    value={settings.fonts.primary}
                    onChange={(e) => updateSettings({ fonts: { ...settings.fonts, primary: e.target.value } })}
                    className="w-full rounded-lg border-slate-200 px-3 py-2 text-sm"
                  >
                    {fontOptions.map((f) => (
                      <option key={f} value={f}>{f}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-900">Secondary font</label>
                  <select
                    value={settings.fonts.secondary}
                    onChange={(e) => updateSettings({ fonts: { ...settings.fonts, secondary: e.target.value } })}
                    className="w-full rounded-lg border-slate-200 px-3 py-2 text-sm"
                  >
                    {fontOptions.map((f) => (
                      <option key={f} value={f}>{f}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="space-y-4">
                {[
                  { label: 'Heading size', key: 'heading' },
                  { label: 'Body size', key: 'body' },
                ].map((item) => (
                  <div key={item.key} className="space-y-1">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-slate-600">{item.label}</span>
                      <span className="font-medium text-slate-800">{(settings.sizes as any)[item.key]}%</span>
                    </div>
                    <input
                      type="range"
                      min="50"
                      max="200"
                      value={(settings.sizes as any)[item.key]}
                      onChange={(e) => updateSettings({ sizes: { ...settings.sizes, [item.key]: parseInt(e.target.value) } })}
                      className="w-full"
                    />
                  </div>
                ))}
                <div className="space-y-1">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-slate-600">Line spacing</span>
                    <span className="font-medium text-slate-800">{settings.spacing.lineSpacing}</span>
                  </div>
                  <input
                    type="range"
                    min="0.8"
                    max="2.5"
                    step="0.1"
                    value={settings.spacing.lineSpacing}
                    onChange={(e) => updateSettings({ spacing: { ...settings.spacing, lineSpacing: parseFloat(e.target.value) } })}
                    className="w-full"
                  />
                </div>
              </div>

              <div className="space-y-3">
                <h3 className="text-sm font-semibold text-slate-900">Reorder sections</h3>
                <p className="text-xs text-slate-500">Personal information stays at top. Use arrows to reorder.</p>
                <div className="space-y-2">
                  {settings.sectionOrder.map((section, index) => (
                    <div key={section} className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 bg-slate-50">
                      <GripVertical className="h-4 w-4 text-slate-400" />
                      <span className="flex-1 text-sm text-slate-700">{sectionLabels[section] || section}</span>
                      <div className="flex gap-1">
                        <button
                          onClick={() => moveSection(index, -1)}
                          disabled={index === 0}
                          className="p-1 text-slate-500 hover:text-slate-700 disabled:opacity-30"
                        >
                          ↑
                        </button>
                        <button
                          onClick={() => moveSection(index, 1)}
                          disabled={index === settings.sectionOrder.length - 1}
                          className="p-1 text-slate-500 hover:text-slate-700 disabled:opacity-30"
                        >
                          ↓
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
